<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import AgentChat from './AgentChat.vue'

// RM AI launcher: presents the RM AI chat (AgentChat) as a partial-height
// bottom sheet ("pulled up ~3/4") over the Discover landing for Platinum
// members. Tapping the input or a starter chip expands it to full height; the
// scrim, close button, or expanding-then-closing returns the member to
// Discover underneath. The chat is only mounted while open, so no socket
// connection is opened until the member actually engages.
const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const expanded = ref(false)

function close() {
  emit('update:modelValue', false)
}

// Lock the page behind the sheet so wheel/touch scrolling stays inside the
// launcher instead of scrolling Discover underneath. (VNavigationDrawer does
// not block document scroll on its own.)
function setBodyScrollLock(locked) {
  document.documentElement.style.overflow = locked ? 'hidden' : ''
  document.body.style.overflow = locked ? 'hidden' : ''
}

// `immediate` so a sheet that mounts already-open (the parent sets the model
// synchronously on landing) still locks scroll on first render.
watch(() => props.modelValue, open => {
  if (open) expanded.value = false
  setBodyScrollLock(open)
}, { immediate: true })

onBeforeUnmount(() => setBodyScrollLock(false))
</script>

<template>
  <VNavigationDrawer
    :model-value="modelValue"
    location="bottom"
    temporary
    class="rm-ai-launcher-drawer"
    :class="{ 'rm-ai-launcher-drawer--expanded': expanded }"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="rm-ai-launcher">
      <!-- Grab handle floating over the chat header's teal (no seam) -->
      <div
        class="rm-ai-launcher__handle"
        aria-hidden="true"
      />
      <AgentChat
        v-if="modelValue"
        sheet-mode
        @close="close"
        @expand="expanded = true"
      />
    </div>
  </VNavigationDrawer>
</template>

<style scoped>
.rm-ai-launcher-drawer {
  height: 78vh !important;
  max-height: 100dvh;
  border: none !important;
  border-top-left-radius: var(--ds-radius-lg, 16px);
  border-top-right-radius: var(--ds-radius-lg, 16px);
  overflow: hidden;
  transition: height 0.25s ease;
}

.rm-ai-launcher-drawer--expanded {
  height: 100dvh !important;
  border-radius: 0;
}

.rm-ai-launcher {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.rm-ai-launcher__handle {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}
</style>
